import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { Star, MessageSquare } from "lucide-react";
import { format } from "date-fns";

interface FeedbackSession {
  id: string;
  user_first_name: string;
  user_last_name: string;
  user_location: string | null;
  feedback_rating: number | null;
  feedback_comment: string | null;
  created_at: string;
  regions?: {
    name: string;
  };
}

export const ChatFeedbackManager = () => {
  const [feedbackSessions, setFeedbackSessions] = useState<FeedbackSession[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFeedback();
  }, []);

  const fetchFeedback = async () => {
    try {
      const { data, error } = await supabase
        .from("chat_sessions")
        .select(`
          *,
          regions (
            name
          )
        `)
        .not("feedback_rating", "is", null)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setFeedbackSessions(data || []);
    } catch (error) {
      console.error("Error fetching feedback:", error);
    } finally {
      setLoading(false);
    }
  };

  const renderStars = (rating: number) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-4 w-4 ${
              star <= rating
                ? "fill-ministry-gold text-ministry-gold"
                : "text-muted-foreground"
            }`}
          />
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-center text-muted-foreground">Loading feedback...</p>
        </CardContent>
      </Card>
    );
  }

  const averageRating = feedbackSessions.length > 0
    ? (feedbackSessions.reduce((sum, s) => sum + (s.feedback_rating || 0), 0) / feedbackSessions.length).toFixed(1)
    : "0";

  return (
    <div className="space-y-6">
      {/* Summary Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5" />
            Chatbot Feedback Summary
          </CardTitle>
          <CardDescription>User satisfaction ratings and comments</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-8">
            <div>
              <p className="text-sm text-muted-foreground">Total Feedback</p>
              <p className="text-3xl font-bold">{feedbackSessions.length}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Average Rating</p>
              <div className="flex items-center gap-2">
                <p className="text-3xl font-bold">{averageRating}</p>
                <Star className="h-6 w-6 fill-ministry-gold text-ministry-gold" />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Feedback Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Feedback</CardTitle>
        </CardHeader>
        <CardContent>
          {feedbackSessions.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              No feedback submitted yet.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Region</TableHead>
                    <TableHead>Rating</TableHead>
                    <TableHead>Comment</TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {feedbackSessions.map((session) => (
                    <TableRow key={session.id}>
                      <TableCell className="font-medium">
                        {session.user_first_name} {session.user_last_name}
                      </TableCell>
                      <TableCell>
                        {session.regions?.name ? (
                          <Badge variant="secondary">{session.regions.name}</Badge>
                        ) : (
                          <span className="text-muted-foreground text-sm">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {session.feedback_rating && renderStars(session.feedback_rating)}
                      </TableCell>
                      <TableCell className="max-w-md">
                        {session.feedback_comment ? (
                          <p className="text-sm line-clamp-2">{session.feedback_comment}</p>
                        ) : (
                          <span className="text-muted-foreground text-sm">No comment</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {format(new Date(session.created_at), "MMM d, yyyy")}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
